/**
 * Membership-settings form (v2 — DNKADM-0056 U2).
 *
 * Single-record edit form for `GET/PUT /v2/admin/membership-settings` (§5.10), seeded
 * from the loaded view and saved through `putMembershipSettings`. Fields:
 *  - **Grace period** — integer days ≥ 0;
 *  - **Pause membership credits when a payment fails** — on/off (DNKAPI-0704, default on);
 *  - **Max reschedules** — integer ≥ 0 (empty/`0` = unlimited); the form sends `0` for
 *    unlimited;
 *  - **Refund rounding increment** — decimal > 0;
 *  - **Rounding mode** — a vertical `RadioGroup` over the OFFERED `RoundingMode` names.
 *
 * **Removed vs v1 (§9):** the `creditsPerHour` control (credit valuation is now an
 * explicit per-variation `creditCost`, 0055) and the global daily-cap control
 * (`dailySchedulingCapHours` — now the per-(plan,bucket) daily-cap matrix, 0057). This
 * card neither renders nor submits either.
 *
 * Client-side range validation mirrors the server; a failed field shows its error and
 * blocks Save. On a successful save the parent resource is `refetch`ed and a success
 * toast is raised. A validation `400` renders INLINE; an upstream/`502` raises an error
 * toast (see `settingsError`).
 */
import { useId, useState, type ReactNode } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { MONO, resolveTokens } from '@/theme';
import {
  REFUND_ROUNDING_MODES,
  type MembershipSettingsView,
  type RefundRoundingMode,
  putMembershipSettings,
} from '@/api/membershipSettings';
import type { ShowToast } from '@/components/toastContext';
import { classifyFormError } from '@/api/screenError';

interface MembershipSettingsCardProps {
  /** The loaded membership settings. */
  data: MembershipSettingsView;
  /** Re-fetch after a successful save (refetch-after-write). */
  refetch: () => void;
  /** Raise a non-blocking toast (success / upstream-error). */
  showToast: ShowToast;
}

/** Human label for each rounding mode (UI label → enum value mapping). */
const MODE_LABELS: Record<RefundRoundingMode, string> = {
  UP: 'Up',
  DOWN: 'Down',
  CEILING: 'Ceiling',
  FLOOR: 'Floor',
  HALF_UP: 'Half up',
  HALF_DOWN: 'Half down',
  HALF_EVEN: 'Half even',
  UNNECESSARY: 'Unnecessary',
};

/**
 * Plain-language description for each rounding mode, framed for "a partial refund that
 * lands between rounding steps". Shown as secondary text under the mode name so the
 * choice is legible to a non-mathematician.
 */
const MODE_DESCRIPTIONS: Record<RefundRoundingMode, string> = {
  HALF_UP: 'Round to the nearest step; ties round up. (Most common.)',
  HALF_DOWN: 'Round to the nearest step; ties round down.',
  HALF_EVEN:
    "Round to the nearest step; ties go to the even step (banker's rounding — avoids bias over time).",
  UP: 'Always round up to the next step (favors the member).',
  CEILING: 'Always round up to the next step (same as Up for refunds, which are never negative).',
  DOWN: 'Always round down to the lower step (favors the house).',
  FLOOR: 'Always round down to the lower step (same as Down for refunds).',
  UNNECESSARY: 'No rounding — a refund must already land exactly on a step, or it is rejected.',
};

interface FieldErrors {
  graceDays?: string;
  refundRoundingIncrement?: string;
  maxReschedules?: string;
}

/**
 * Pre-fill string for the reschedule cap: `null`/`0` (unlimited) seeds an empty field
 * (the placeholder/helper render "Unlimited"), any other value its number.
 */
function maxReschedulesField(value: number | null): string {
  return value === null || value === 0 ? '' : String(value);
}

/**
 * Validate the numeric fields against the server's ranges. Returns the parsed numbers
 * when every field is valid, else the per-field error messages.
 */
function validate(
  graceDays: string,
  refundRoundingIncrement: string,
  maxReschedules: string,
):
  | { ok: true; values: { graceDays: number; refundRoundingIncrement: number; maxReschedules: number } }
  | { ok: false; errors: FieldErrors } {
  const errors: FieldErrors = {};

  const grace = Number(graceDays);
  if (graceDays.trim() === '' || !Number.isFinite(grace) || !Number.isInteger(grace) || grace < 0) {
    errors.graceDays = 'Enter a whole number of days, 0 or more.';
  }

  const inc = Number(refundRoundingIncrement);
  if (refundRoundingIncrement.trim() === '' || !Number.isFinite(inc) || inc <= 0) {
    errors.refundRoundingIncrement = 'Enter a number greater than 0.';
  }

  // Reschedule cap: empty = unlimited (sent as 0); otherwise an integer ≥ 0.
  const emptyCap = maxReschedules.trim() === '';
  const cap = emptyCap ? 0 : Number(maxReschedules);
  if (!emptyCap && (!Number.isFinite(cap) || !Number.isInteger(cap) || cap < 0)) {
    errors.maxReschedules = 'Enter a whole number, 0 or more (0 or empty = unlimited).';
  }

  if (errors.graceDays || errors.refundRoundingIncrement || errors.maxReschedules) {
    return { ok: false, errors };
  }
  return {
    ok: true,
    values: { graceDays: grace, refundRoundingIncrement: inc, maxReschedules: cap },
  };
}

export function MembershipSettingsCard({ data, refetch, showToast }: MembershipSettingsCardProps) {
  const ids = useId();

  // Numeric fields edit as strings so an in-progress entry (e.g. "0.") is not
  // clobbered; they are parsed + range-checked on Save.
  const [graceDays, setGraceDays] = useState(String(data.graceDays));
  const [refundRoundingIncrement, setRefundRoundingIncrement] = useState(
    String(data.refundRoundingIncrement),
  );
  const [refundRoundingMode, setRefundRoundingMode] = useState<RefundRoundingMode>(
    data.refundRoundingMode,
  );
  const [maxReschedules, setMaxReschedules] = useState(maxReschedulesField(data.maxReschedules));
  const [blockOnPaymentFailure, setBlockOnPaymentFailure] = useState(
    data.blockMembershipCreditsOnPaymentFailure,
  );

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setServerError(null);
    const result = validate(graceDays, refundRoundingIncrement, maxReschedules);
    if (!result.ok) {
      setFieldErrors(result.errors);
      return;
    }
    setFieldErrors({});
    setSaving(true);
    try {
      await putMembershipSettings({
        graceDays: result.values.graceDays,
        maxReschedules: result.values.maxReschedules,
        refundRoundingIncrement: result.values.refundRoundingIncrement,
        refundRoundingMode,
        blockMembershipCreditsOnPaymentFailure: blockOnPaymentFailure,
      });
      // Refetch-after-write: the form reseeds from the freshly saved view.
      refetch();
      showToast('Membership settings saved', 'success');
    } catch (err) {
      const view = classifyFormError(err, 'That value isn’t valid — check the highlighted fields.', {
        toastFallback: 'The settings couldn’t be saved. Please try again.',
        conflictInline: false,
      });
      if (view.kind === 'inline') setServerError(view.message);
      else showToast(view.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  const graceId = `${ids}-grace`;
  const incId = `${ids}-inc`;
  const maxId = `${ids}-max`;
  const modeLabelId = `${ids}-mode-label`;

  return (
    <Box>
      <Paper elevation={1} sx={{ px: 2.75, py: 0.75 }}>
        <SettingRow label="Grace period" hint="Days after expiry before a membership lapses">
          <TextField
            id={graceId}
            label="Grace period"
            hiddenLabel
            value={graceDays}
            onChange={(e) => setGraceDays(e.target.value)}
            disabled={saving}
            error={Boolean(fieldErrors.graceDays)}
            helperText={fieldErrors.graceDays}
            sx={{ width: 150 }}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">days</InputAdornment>,
                sx: { fontFamily: MONO },
              },
              htmlInput: { inputMode: 'numeric', 'aria-label': 'Grace period (days)' },
            }}
          />
        </SettingRow>

        <SettingRow
          label="Pause membership credits when a payment fails"
          hint="While a member's membership payment has failed, they can't book with membership credits until their card is updated. Package credits, comps and card bookings still work."
        >
          <Switch
            checked={blockOnPaymentFailure}
            onChange={(e) => setBlockOnPaymentFailure(e.target.checked)}
            disabled={saving}
            slotProps={{
              input: { 'aria-label': 'Pause membership credits when a payment fails' },
            }}
          />
        </SettingRow>

        <SettingRow
          label="Max reschedules"
          hint="Cap on member/guest reschedules of a single reservation"
        >
          <TextField
            id={maxId}
            label="Max reschedules"
            hiddenLabel
            value={maxReschedules}
            onChange={(e) => setMaxReschedules(e.target.value)}
            disabled={saving}
            placeholder="Unlimited"
            error={Boolean(fieldErrors.maxReschedules)}
            helperText={fieldErrors.maxReschedules ?? '0 or empty = unlimited'}
            sx={{ width: 150 }}
            slotProps={{
              input: { sx: { fontFamily: MONO } },
              htmlInput: { inputMode: 'numeric', 'aria-label': 'Max reschedules' },
            }}
          />
        </SettingRow>

        <SettingRow label="Refund rounding increment" hint="Partial refunds round to this step">
          <TextField
            id={incId}
            label="Refund rounding increment"
            hiddenLabel
            value={refundRoundingIncrement}
            onChange={(e) => setRefundRoundingIncrement(e.target.value)}
            disabled={saving}
            error={Boolean(fieldErrors.refundRoundingIncrement)}
            helperText={fieldErrors.refundRoundingIncrement}
            sx={{ width: 150 }}
            slotProps={{
              input: { sx: { fontFamily: MONO } },
              htmlInput: { inputMode: 'decimal', 'aria-label': 'Refund rounding increment' },
            }}
          />
        </SettingRow>

        <SettingRow label="Rounding mode" hint="Direction when rounding partial refunds" last>
          <FormControl component="fieldset" variant="standard">
            <Typography
              id={modeLabelId}
              sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}
            >
              Rounding mode
            </Typography>
            <RadioGroup
              aria-labelledby={modeLabelId}
              value={refundRoundingMode}
              onChange={(_e, value) => setRefundRoundingMode(value as RefundRoundingMode)}
            >
              {REFUND_ROUNDING_MODES.map((mode) => (
                <FormControlLabel
                  key={mode}
                  value={mode}
                  disabled={saving}
                  sx={{ alignItems: 'flex-start', mr: 0, mb: 0.75 }}
                  control={<Radio size="small" sx={{ pt: 0.25 }} />}
                  label={
                    <Box sx={{ pt: 0.25 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>
                        {MODE_LABELS[mode]}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {MODE_DESCRIPTIONS[mode]}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>
        </SettingRow>
      </Paper>
      {serverError && (
        <Box role="alert" sx={{ mt: 1.5, fontSize: 13, color: 'error.main', fontWeight: 500 }}>
          {serverError}
        </Box>
      )}
      <Stack direction="row" sx={{ justifyContent: 'flex-end', mt: 1.75 }}>
        <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Saving…' : 'Save settings'}
        </Button>
      </Stack>
    </Box>
  );
}

/** One label/hint + control row in the settings card. */
function SettingRow({
  label,
  hint,
  last,
  children,
}: {
  label: string;
  hint: string;
  last?: boolean;
  children: ReactNode;
}) {
  const t = resolveTokens(useTheme());
  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        py: 2,
        borderBottom: last ? 'none' : '1px solid',
        borderColor: t.lineFaint,
      }}
    >
      <Box sx={{ flex: 1, pt: 0.5 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>{label}</Typography>
        <Typography variant="body2" sx={{ mt: 0.25 }}>
          {hint}
        </Typography>
      </Box>
      {children}
    </Stack>
  );
}
